/**
 * Persist sort preferences (field + direction) in `localStorage` per page.
 *
 * Keeps the standalone configuration list pages (App Configuration, PPPC, …)
 * in sync with each other: same default direction (`desc`), same storage
 * shape, same validation that gracefully falls back to the default if the
 * stored value is corrupt or no longer valid.
 *
 * The generic parameter `TField extends string` is whatever sort field a
 * given page exposes (`"name" | "app" | "keys" | "updated"` for App Config,
 * `"name" | "identifier" | "services" | "updated"` for PPPC, etc.).
 */
import { useEffect, useState } from "react";
import type { SortDirection } from "@/components/configurations/SortControls";

export interface SortPreference<TField extends string> {
  field: TField;
  direction: SortDirection;
}

export function usePersistentSort<TField extends string>(
  storageKey: string,
  defaults: SortPreference<TField>,
  validFields: ReadonlyArray<TField>,
) {
  const [sort, setSort] = useState<SortPreference<TField>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw) as Partial<SortPreference<TField>>;
      const field = validFields.includes(parsed.field as TField)
        ? (parsed.field as TField)
        : defaults.field;
      const direction =
        parsed.direction === "asc" || parsed.direction === "desc"
          ? parsed.direction
          : defaults.direction;
      return { field, direction };
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(sort));
    } catch {
      // Storage may be unavailable (private mode, quota); degrade silently.
    }
  }, [storageKey, sort]);

  const setField = (field: TField) => setSort((s) => ({ ...s, field }));
  const setDirection = (direction: SortDirection) =>
    setSort((s) => ({ ...s, direction }));
  const toggleDirection = () =>
    setSort((s) => ({
      ...s,
      direction: s.direction === "asc" ? "desc" : "asc",
    }));

  return { sort, setSort, setField, setDirection, toggleDirection };
}

/**
 * Locale-aware string compare used by every sort surface so "App 2" sorts
 * before "App 10" and casing differences don't reorder rows.
 */
export const compareSortString = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

/** Coerce an ISO timestamp into milliseconds, returning 0 for missing/invalid. */
export const toSortTime = (iso?: string): number => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
};
